// src/pages/ventes/NouveauReglement.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Form, Button, Spinner, Alert,
    InputGroup, Badge, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

export default function NouveauReglement() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const factureId = searchParams.get('facture');
    const clientId = searchParams.get('client');

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(!!factureId || !!clientId);
    const [clients, setClients] = useState([]);
    const [factures, setFactures] = useState([]);
    const [searchClient, setSearchClient] = useState('');
    const [showClientResults, setShowClientResults] = useState(false);
    const [formData, setFormData] = useState({
        id_client: clientId || '',
        client_nom: '',
        id_facture: factureId || '',
        date_reglement: new Date().toISOString().split('T')[0],
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        commentaire: ''
    });
    const [errors, setErrors] = useState({});
    const [clientInfo, setClientInfo] = useState(null);
    const [factureInfo, setFactureInfo] = useState(null);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        fetchClients();
    }, []);

    useEffect(() => {
        if (clientId) loadClientById(clientId);
        if (factureId) loadFactureById(factureId);
    }, [clientId, factureId]);

    useEffect(() => {
        if (formData.id_client) fetchFacturesImpayees(formData.id_client);
    }, [formData.id_client]);

    /* ============================================================
       FETCH
       ============================================================ */
    const loadClientById = async (id) => {
        try {
            setLoadingData(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClientInfo(result.data);
                setFormData(prev => ({
                    ...prev,
                    id_client: result.data.id_client,
                    client_nom: result.data.raison_sociale,
                    client_code: result.data.code_client
                }));
                setSearchClient(result.data.raison_sociale);
            }
        } catch (err) {
            console.error("Erreur chargement client", err);
        } finally {
            setLoadingData(false);
        }
    };

    const loadFactureById = async (id) => {
        try {
            setLoadingData(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setFactureInfo(result.data);
                if (result.data && result.data.solde) {
                    setFormData(prev => ({ ...prev, montant: result.data.solde }));
                }
            }
        } catch (err) {
            console.error("Erreur chargement facture", err);
        } finally {
            setLoadingData(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setClients(result.data || []);
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchFacturesImpayees = async (idClient) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?action=factures_impayees&id_client=${idClient}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            setFactures(result.data || []);
            if (factureId) setFormData(prev => ({ ...prev, id_facture: factureId }));
        } catch (err) {
            console.error("Erreur chargement factures", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleClientSelect = (client) => {
        setFormData({
            ...formData,
            id_client: client.id_client,
            client_nom: client.raison_sociale,
            client_code: client.code_client,
            id_facture: ''
        });
        setSearchClient(client.raison_sociale);
        setShowClientResults(false);
        setClientInfo(client);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: null });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.id_client) newErrors.id_client = "Veuillez sélectionner un client";
        if (!formData.montant) newErrors.montant = "Le montant est requis";
        else if (parseFloat(formData.montant) <= 0) newErrors.montant = "Le montant doit être supérieur à 0";
        if (!formData.mode_reglement) newErrors.mode_reglement = "Le mode de règlement est requis";
        if (formData.id_facture) {
            const facture = factures.find(f => f.id_facture === parseInt(formData.id_facture));
            if (facture && parseFloat(formData.montant) > facture.solde) {
                newErrors.montant = `Le montant ne peut pas dépasser le solde (${formatCurrency(facture.solde)})`;
            }
        }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showToast('error', 'Champ requis', 'Veuillez corriger les erreurs du formulaire');
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const response = await fetch(`${API_BASE_URL}/reglements.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create',
                    ...formData,
                    montant: parseFloat(formData.montant),
                    id_utilisateur: user.id_utilisateur || 1
                })
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Règlement créé', 'Le règlement a été enregistré avec succès');
                setTimeout(() => navigate('/ventes/reglements'), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création");
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur de connexion au serveur");
        } finally {
            setLoading(false);
        }
    };

    const factureSelectionnee = factures.find(f => f.id_facture === parseInt(formData.id_facture)) || factureInfo;

    const modeOptions = [
        { value: 'especes', label: 'Espèces', icon: 'ri-money-euro-circle-line' },
        { value: 'cheque', label: 'Chèque', icon: 'ri-bank-card-line' },
        { value: 'virement', label: 'Virement bancaire', icon: 'ri-building-line' },
        { value: 'carte_bancaire', label: 'Carte bancaire', icon: 'ri-smartphone-line' },
        { value: 'traite', label: 'Traite', icon: 'ri-file-copy-line' }
    ];

    /* ============================================================
       LOADING
       ============================================================ */
    if (loadingData) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-bank-card-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des données
                        </h5>
                        <p className="text-muted small">Récupération des informations en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Nouveau règlement</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-money-euro-circle-line"></i>
                                        Saisie d'un paiement
                                    </span>
                                    {(factureId || clientId) && (
                                        <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                            <i className="ri-magic-line"></i>
                                            Pré-rempli
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Button className="btn-modern-outline" onClick={() => navigate(-1)}>
                            <i className="ri-arrow-left-line"></i> Retour
                        </Button>
                    </div>

                    {/* ===== BANNIÈRE PRÉ-REMPLISSAGE ===== */}
                    {(factureId || clientId) && (
                        <div className="banner-info-charte mb-4">
                            <div className="banner-icon-charte">
                                <i className="ri-information-line"></i>
                            </div>
                            <div>
                                <strong className="d-block">Formulaire pré-rempli</strong>
                                <p className="mb-0 small">
                                    {clientInfo && `Client: ${clientInfo.raison_sociale} `}
                                    {factureInfo && `— Facture: ${factureInfo.numero_facture} (Solde: ${formatCurrency(factureInfo.solde)})`}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ===== FORMULAIRE ===== */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="card-header-gradient">
                            <h5 className="mb-0 text-white">
                                <i className="ri-bank-card-line me-2"></i>
                                Informations de paiement
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">

                                    {/* ===== COLONNE GAUCHE ===== */}
                                    <Col md={6}>

                                        {/* Recherche client */}
                                        <Form.Group className="mb-4 position-relative">
                                            <Form.Label className="form-label-modern">
                                                Client <span className="text-danger">*</span>
                                            </Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Rechercher un client..."
                                                    value={searchClient}
                                                    onChange={(e) => {
                                                        setSearchClient(e.target.value);
                                                        setShowClientResults(true);
                                                    }}
                                                    onFocus={() => setShowClientResults(true)}
                                                    isInvalid={!!errors.id_client}
                                                    disabled={!!clientId}
                                                />
                                            </InputGroup>
                                            <Form.Control.Feedback type="invalid" style={{ display: errors.id_client ? 'block' : 'none' }}>
                                                {errors.id_client}
                                            </Form.Control.Feedback>

                                            {showClientResults && searchClient && !clientId && (
                                                <div className="client-search-dropdown-charte">
                                                    {clients
                                                        .filter(c =>
                                                            c.raison_sociale?.toLowerCase().includes(searchClient.toLowerCase()) ||
                                                            c.code_client?.toLowerCase().includes(searchClient.toLowerCase())
                                                        )
                                                        .map(client => (
                                                            <div
                                                                key={client.id_client}
                                                                className="client-search-item-charte"
                                                                onClick={() => handleClientSelect(client)}
                                                            >
                                                                <div className="client-search-avatar-charte">
                                                                    <i className="ri-user-line"></i>
                                                                </div>
                                                                <div className="client-search-info-charte">
                                                                    <strong>{client.raison_sociale}</strong>
                                                                    <small>{client.code_client} | {client.telephone || 'Tél non renseigné'}</small>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </Form.Group>

                                        {/* Client sélectionné */}
                                        {formData.id_client && (
                                            <div className="client-selected-charte mb-4">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="client-avatar-small-charte">
                                                            <i className="ri-user-line"></i>
                                                        </div>
                                                        <div>
                                                            <strong className="d-block" style={{ color: COLORS.primaryDark }}>
                                                                {formData.client_nom}
                                                            </strong>
                                                            <small className="text-muted">
                                                                <i className="ri-barcode-line me-1"></i>
                                                                Code: {formData.client_code}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    {!clientId && (
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Retirer</Tooltip>}>
                                                            <button
                                                                className="btn-remove-charte"
                                                                onClick={() => {
                                                                    setFormData({ ...formData, id_client: '', client_nom: '', id_facture: '' });
                                                                    setSearchClient('');
                                                                    setFactures([]);
                                                                }}
                                                            >
                                                                <i className="ri-close-line"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Sélection facture */}
                                        <Form.Group className="mb-4">
                                            <Form.Label className="form-label-modern">Facture (optionnel)</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-file-list-3-line"></i></InputGroup.Text>
                                                <Form.Select
                                                    name="id_facture"
                                                    value={formData.id_facture}
                                                    onChange={handleChange}
                                                    disabled={!formData.id_client || factures.length === 0 || !!factureId}
                                                >
                                                    <option value="">Sélectionnez une facture</option>
                                                    {factures.map(facture => (
                                                        <option key={facture.id_facture} value={facture.id_facture}>
                                                            {facture.numero_facture} — {formatCurrency(facture.solde)} restant
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </InputGroup>
                                            {!formData.id_client && (
                                                <Form.Text className="text-muted small">
                                                    <i className="ri-information-line me-1"></i>
                                                    Sélectionnez d'abord un client
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    {/* ===== COLONNE DROITE ===== */}
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="form-label-modern">
                                                Date de règlement <span className="text-danger">*</span>
                                            </Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="date"
                                                    name="date_reglement"
                                                    value={formData.date_reglement}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </InputGroup>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="form-label-modern">
                                                Mode de règlement <span className="text-danger">*</span>
                                            </Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-bank-line"></i></InputGroup.Text>
                                                <Form.Select
                                                    name="mode_reglement"
                                                    value={formData.mode_reglement}
                                                    onChange={handleChange}
                                                    isInvalid={!!errors.mode_reglement}
                                                    required
                                                >
                                                    {modeOptions.map(mode => (
                                                        <option key={mode.value} value={mode.value}>
                                                            {mode.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </InputGroup>
                                            <Form.Control.Feedback type="invalid" style={{ display: errors.mode_reglement ? 'block' : 'none' }}>
                                                {errors.mode_reglement}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="form-label-modern">Référence (optionnel)</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="reference"
                                                    value={formData.reference}
                                                    onChange={handleChange}
                                                    placeholder="N° chèque, référence virement..."
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    {/* ===== RÉCAP FACTURE + MONTANT ===== */}
                                    <Col md={6}>
                                        {factureSelectionnee && (
                                            <div className="facture-summary-charte">
                                                <div className="summary-header-charte">
                                                    <i className="ri-file-list-3-line"></i>
                                                    <span>Facture associée</span>
                                                </div>
                                                <div className="summary-body-charte">
                                                    <div className="summary-row-charte">
                                                        <span className="summary-label-charte">
                                                            <i className="ri-barcode-line"></i>Numéro
                                                        </span>
                                                        <span className="summary-value-charte fw-semibold">
                                                            {factureSelectionnee.numero_facture}
                                                        </span>
                                                    </div>
                                                    <div className="summary-row-charte">
                                                        <span className="summary-label-charte">
                                                            <i className="ri-calculator-line"></i>Montant TTC
                                                        </span>
                                                        <span className="summary-value-charte fw-semibold">
                                                            {formatCurrency(factureSelectionnee.montant_ttc)}
                                                        </span>
                                                    </div>
                                                    <div className="summary-row-charte">
                                                        <span className="summary-label-charte">
                                                            <i className="ri-bank-card-line"></i>Déjà réglé
                                                        </span>
                                                        <span className="summary-value-charte" style={{ color: COLORS.success, fontWeight: 600 }}>
                                                            {formatCurrency(factureSelectionnee.montant_regle || 0)}
                                                        </span>
                                                    </div>
                                                    <div className="summary-divider-charte"></div>
                                                    <div className="summary-row-charte highlight">
                                                        <span className="summary-label-charte fw-bold">
                                                            <i className="ri-funds-line"></i>Solde restant
                                                        </span>
                                                        <span className="summary-value-charte fw-bold" style={{ color: COLORS.primary, fontSize: '1.15rem' }}>
                                                            {formatCurrency(factureSelectionnee.solde)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="form-label-modern">
                                                Montant <span className="text-danger">*</span>
                                            </Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    name="montant"
                                                    value={formData.montant}
                                                    onChange={handleChange}
                                                    placeholder="0"
                                                    min="0"
                                                    step="0.01"
                                                    isInvalid={!!errors.montant}
                                                    required
                                                />
                                                <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Control.Feedback type="invalid" style={{ display: errors.montant ? 'block' : 'none' }}>
                                                {errors.montant}
                                            </Form.Control.Feedback>
                                            {factureSelectionnee && (
                                                <Form.Text className="text-muted small">
                                                    <i className="ri-information-line me-1"></i>
                                                    Solde de la facture: {formatCurrency(factureSelectionnee.solde)}
                                                </Form.Text>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="form-label-modern">Commentaire</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="commentaire"
                                                value={formData.commentaire}
                                                onChange={handleChange}
                                                rows={3}
                                                placeholder="Informations complémentaires..."
                                                className="textarea-modern"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* ===== ACTIONS ===== */}
                                    <Col md={12}>
                                        <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                        <div className="d-flex justify-content-end gap-3 flex-wrap">
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => navigate('/ventes/reglements')}
                                                className="btn-modern-outline"
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={loading}
                                                className="btn-modern-primary"
                                            >
                                                {loading ? (
                                                    <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                                ) : (
                                                    <><i className="ri-save-line me-2"></i>Enregistrer le règlement</>
                                                )}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== BANNIÈRE ===== */
                .banner-info-charte {
                    display: flex; align-items: center; gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: linear-gradient(135deg, #eff6ff, #dbeafe);
                    border-left: 4px solid var(--info);
                    border-radius: 12px;
                    box-shadow: 0 4px 16px rgba(59,130,246,0.08);
                }
                .banner-icon-charte {
                    width: 44px; height: 44px; border-radius: 12px;
                    background: rgba(59,130,246,0.15);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--info); font-size: 1.4rem;
                    flex-shrink: 0;
                }
                .banner-info-charte strong {
                    color: #1e40af; font-size: 0.95rem;
                }
                .banner-info-charte p { color: #1e40af; }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }
                .textarea-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.8rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                    resize: vertical;
                }
                .textarea-modern:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== CLIENT SEARCH DROPDOWN ===== */
                .client-search-dropdown-charte {
                    position: absolute;
                    top: 100%;
                    left: 0; right: 0;
                    margin-top: 4px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.1);
                    z-index: 1000;
                    max-height: 300px;
                    overflow-y: auto;
                    animation: slideDown 0.2s ease-out;
                }
                .client-search-dropdown-charte::-webkit-scrollbar { width: 6px; }
                .client-search-dropdown-charte::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .client-search-dropdown-charte::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .client-search-item-charte {
                    padding: 0.85rem 1rem;
                    border-bottom: 1px solid #f1f5f9;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                }
                .client-search-item-charte:last-child { border-bottom: none; }
                .client-search-item-charte:hover {
                    background: linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.secondary}08);
                }
                .client-search-avatar-charte {
                    width: 40px; height: 40px; border-radius: 10px;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                    flex-shrink: 0;
                }
                .client-search-info-charte {
                    flex: 1; min-width: 0;
                }
                .client-search-info-charte strong {
                    display: block;
                    color: #1e293b;
                    font-size: 0.9rem;
                    margin-bottom: 2px;
                }
                .client-search-info-charte small {
                    color: #94a3b8;
                    font-size: 0.75rem;
                }

                /* ===== CLIENT SELECTED ===== */
                .client-selected-charte {
                    background: linear-gradient(135deg, #ffffff, #f8fafc);
                    border: 2px solid ${COLORS.secondary}25;
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    transition: all 0.2s ease;
                }
                .client-selected-charte:hover {
                    border-color: var(--secondary);
                    box-shadow: 0 4px 12px rgba(33,172,205,0.1);
                }
                .client-avatar-small-charte {
                    width: 44px; height: 44px; border-radius: 12px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    color: var(--secondary);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.2rem;
                    flex-shrink: 0;
                }
                .btn-remove-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    background: white;
                    border: 1.5px solid #e2e8f0;
                    color: var(--danger);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .btn-remove-charte:hover {
                    background: var(--danger);
                    border-color: var(--danger);
                    color: white;
                    transform: scale(1.05);
                }

                /* ===== FACTURE SUMMARY ===== */
                .facture-summary-charte {
                    background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
                    transition: all 0.3s ease;
                    animation: slideDown 0.3s ease-out;
                }
                .facture-summary-charte:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 28px rgba(41,90,167,0.1);
                }
                .summary-header-charte {
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    padding: 0.85rem 1.25rem;
                    border-bottom: 1px solid #e2e8f0;
                    font-weight: 600;
                    color: var(--primary);
                    display: flex; align-items: center; gap: 8px;
                    font-size: 0.9rem;
                }
                .summary-header-charte i {
                    color: var(--secondary);
                    font-size: 1.1rem;
                }
                .summary-body-charte { padding: 1rem 1.25rem; }
                .summary-row-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.6rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .summary-row-charte:last-of-type { border-bottom: none; }
                .summary-row-charte.highlight {
                    background: linear-gradient(135deg, ${COLORS.primary}08, ${COLORS.secondary}08);
                    margin-top: 0.5rem;
                    padding: 0.85rem 1rem;
                    border-radius: 10px;
                    border-bottom: none;
                }
                .summary-label-charte {
                    display: flex; align-items: center; gap: 8px;
                    color: #64748b;
                    font-size: 0.85rem;
                }
                .summary-label-charte i {
                    font-size: 1rem;
                    color: var(--secondary);
                }
                .summary-value-charte {
                    font-size: 0.9rem;
                    color: #1e293b;
                }
                .summary-divider-charte {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, ${COLORS.primary}30, transparent);
                    margin: 0.75rem 0;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.55rem 1.8rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.7; transform: none; }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .banner-info-charte { flex-direction: column; text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}